import * as Yup from "yup";

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});
const roleSchema = Yup.object().shape({
  name: Yup.string().trim().required("Role Name is required"),
  position_role_id: Yup.string().required("Select a role"),
});

const userSchema = Yup.object({
  name: Yup.string().required("User name is required"),
  username: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().nullable(),
  quotation_unique_code: Yup.string().required("User Code is required"),
  job_title: Yup.string().nullable(),
  organization_ids: Yup.array()
    .of(Yup.string())
    .min(1, "At least one organization is required")
    .required("Organization is required"),
  organization_roles: Yup.lazy((value, options) => {
    const parent = options?.parent;
    const orgIds = parent?.organization_ids || [];
    const shape = {};
    orgIds.forEach((orgId) => {
      shape[orgId] = Yup.string().required("Role is required");
    });
    return Yup.object().shape(shape);
  }),

  password: Yup.string().when("isEdit", {
    is: false,
    then: (schema) =>
      schema.min(6, "Password must be at least 6 characters").required("Password is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const personSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),

  company_id: Yup.object().nullable(),

  designation: Yup.string().trim().nullable(),

  phones: Yup.array().of(
    Yup.string().test(
      "mobile-format",
      "Mobile number is invalid",
      (value) => {
        if (!value) return true;
        const cleaned = value.replace(/[\s\-()]/g, "");
        if (cleaned.startsWith("+91")) {
          return /^\+91[0-9]{10}$/.test(cleaned);
        }
        return /^\+?[0-9]{7,15}$/.test(cleaned);
      }
    )
  ),

  email: Yup.string().email("Enter a valid email address").nullable(),
});

const leadSchema = Yup.object()
  .shape({
    customer_id: Yup.string().required("Customer is required"),

    product_ids: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable(),

    package_id: Yup.mixed().nullable(),

    lead_title: Yup.string().trim().required("Lead title is required"),
    priority_id: Yup.object().nullable(),

    lead_source_id: Yup.object().nullable(),
    lead_status_id: Yup.object().nullable(),
    assigned_to: Yup.object().nullable(),

    budget_amount: Yup.number()
      .typeError("Budget must be a number")
      .nullable()
      .min(0, "Budget cannot be negative"),

    probability: Yup.number()
      .typeError("Probability must be a number")
      .nullable()
      .min(0, "Minimum value is 0")
      .max(100, "Maximum value is 100"),

    lead_notes: Yup.string().trim().nullable(),

    follow_up_create: Yup.boolean(),

    followup_title: Yup.string()
      .trim()
      .nullable()
      .when("follow_up_create", {
        is: true,
        then: (s) => s.required("Follow-up title is required"),
      }),

    followup_type: Yup.mixed().when("follow_up_create", {
      is: true,
      then: (s) => s.required("Follow-up type is required"),
      otherwise: (s) => s.nullable(),
    }),

    followup_description: Yup.string().trim().nullable(),
    next_followup_date: Yup.date()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(new Date(new Date().setHours(0, 0, 0, 0)), "Next followup date cannot be in the past"),


    followup_due_date: Yup.date()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("follow_up_create", {
        is: true,
        then: (s) =>
          s.required("Due date is required").min(new Date(), "Due date cannot be in the past"),
      }),

    company_email: Yup.string()
      .email("Invalid company email")
      .nullable()
      .when("is_new_company", {
        is: true,
        then: (s) => s.required("Company email is required"),
      }),
    company_phone: Yup.string()
      .test(
        "mobile-format",
        "Company phone number is invalid",
        (value) => {
        if (!value) return true;
        const cleaned = value.replace(/[\s\-()]/g, "");
        if (cleaned.startsWith("+91")) {
          return /^\+91[0-9]{10}$/.test(cleaned);
        }
        return /^\+?[0-9]{7,15}$/.test(cleaned);
      }
      )
      .nullable()
      .when("is_new_company", {
        is: true,
        then: (s) => s.required("Company phone is required"),
      }), 
    is_new_company: Yup.boolean(),
    is_new_person: Yup.boolean(),
  })
  .test(
    "product-or-package-required",
    "Either package interest or product interest is required",
    function (value) {
      const hasProducts = Array.isArray(value?.product_ids) && value.product_ids.length > 0;
      const hasPackage = !!value?.package_id;

      if (hasProducts || hasPackage) {
        return true;
      }

      return this.createError({
        path: "product_ids",
        message: "Either package interest or product interest is required",
      });
    }
  );


const customerSchema = Yup.object()
  .shape({
    company_name: Yup.string().trim().nullable(),

    website: Yup.string().trim().url("Enter a valid website URL").nullable(),

    company_phone: Yup.string()
      .test(
        "mobile-format",
        "Mobile number is invalid",
        (value) => {
        if (!value) return true;
        const cleaned = value.replace(/[\s\-()]/g, "");
        if (cleaned.startsWith("+91")) {
          return /^\+91[0-9]{10}$/.test(cleaned);
        }
        return /^\+?[0-9]{7,15}$/.test(cleaned);
      }
      )
      .nullable(),

    company_email: Yup.string().email("Invalid company email").nullable(),

    company_type: Yup.object().nullable(),

    industry: Yup.object().nullable(),

    source: Yup.object().nullable(),

    assigned_to: Yup.object().nullable(),

    gstnumber: Yup.string()
      .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GST number")
      .nullable(),

    contact_person: Yup.string().trim().nullable(),
    person_designation: Yup.string().trim().nullable(),
    mobile: Yup.string()
      .test(
        "mobile-format",
        "Mobile number is invalid",
        (value) => {
        if (!value) return true;
        const cleaned = value.replace(/[\s\-()]/g, "");
        if (cleaned.startsWith("+91")) {
          return /^\+91[0-9]{10}$/.test(cleaned);
        }
        return /^\+?[0-9]{7,15}$/.test(cleaned);
      }
      )
      .nullable(),
    email: Yup.string().email("Enter a valid email address").nullable(),

    billing_street: Yup.string().trim().nullable(),

    billing_city: Yup.object().nullable(),

    billing_zip: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .matches(/^[0-9]{5,6}$/, "Invalid zip code")
      .nullable(),

    shipping_street: Yup.string().trim().nullable(),

    shipping_city: Yup.object().nullable(),

    shipping_zip: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .matches(/^[0-9]{5,6}$/, "Invalid zip code")
      .nullable(),

    copy_billing_address: Yup.boolean(),

    persons: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().trim().nullable(),
          email: Yup.string().email("Enter a valid email address").nullable(),
          phone: Yup.string()
            .test(
              "mobile-format",
              "Mobile number is invalid",
              (value) => {
                if (!value) return true;
                const cleaned = value.replace(/[\s\-()]/g, "");
                if (cleaned.startsWith("+91")) {
                  return /^\+91[0-9]{10}$/.test(cleaned);
                }
                return /^\+?[0-9]{7,15}$/.test(cleaned);
              }
            )
            .nullable(),
          designation: Yup.string().trim().nullable(),
        })
      )
      .nullable(),
  })
  .test(
    "either-name-or-person",
    "Either Company Name or Contact Person is required",
    function (value) {
      if (!value.company_name && !value.contact_person) {
        return this.createError({
          path: "company_name",
          message: "Company Name or Contact Person is required",
        });
      }
      return true;
    }
  );
const quotationSchema = Yup.object().shape({
  customer_person_id: Yup.mixed().nullable(),
  customer_id: Yup.mixed().required("Customer is required"),
  lead_id: Yup.mixed().required("Lead is required"),

  quotation_id: Yup.string().nullable(),
  creation_date: Yup.date().nullable(),
  valid_until: Yup.date().nullable(),
  status_id: Yup.mixed().required("Status is required"),
  assigned_to: Yup.mixed().required("Assigned to is required"),
  package_id: Yup.mixed().nullable(),
  currency: Yup.string().nullable(),
  is_usd: Yup.boolean().nullable(),
  exchange_rate: Yup.number().when("is_usd", {
    is: true,
    then: (schema) => schema.required("Exchange rate is required").typeError("Exchange rate must be a number"),
    otherwise: (schema) => schema.nullable(),
  }),
  add_on_percentage: Yup.number()
    .typeError("Add on must be a number")
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Add on cannot be negative")
    .nullable(),

  // Required totals
  sub_total: Yup.number().typeError("Sub total must be a number").required("Sub total is required"),
  grand_total: Yup.number()
    .typeError("Grand total must be a number")
    .required("Grand total is required"),

  // Discount and Tax
  discount_percentage: Yup.number()
    .typeError("Discount percentage must be a number")
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Discount percentage cannot be negative")
    .max(100, "Discount percentage cannot exceed 100%")
    .nullable(),
  discount_amount: Yup.number().typeError("Discount amount must be a number").nullable(),
  tax_percentage: Yup.number()
    .typeError("Tax percentage must be a number")
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Tax percentage cannot be negative")
    .max(100, "Tax percentage cannot exceed 100%")
    .nullable(),
  tax_amount: Yup.number().typeError("Tax amount must be a number").nullable(),

  delivery_terms: Yup.string().nullable(),
  payment_terms: Yup.string().nullable(),
  other_terms: Yup.string().nullable(),
  terms_and_conditions: Yup.string().nullable(),
  is_gst: Yup.boolean().nullable(),

  items: Yup.array()
    .of(
      Yup.object().shape({
        product_category_id: Yup.mixed().required("Product category is required"),
        sort_order: Yup.number().integer().nullable(),
        sub_total_included: Yup.boolean().nullable(),
        sub_total_value: Yup.number().typeError("Sub total value must be a number").nullable(),
        products: Yup.array()
          .of(
            Yup.object().shape({
              product_id: Yup.mixed().nullable(),
              product_name: Yup.string().nullable(),
              quantity: Yup.number()
                .typeError("Quantity must be a number")
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .min(1, "Quantity must be at least 1")
                .required("Quantity is required"),
              price: Yup.number()
                .typeError("Price must be a number")
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .min(0, "Price must be positive")
                .required("Price is required"),
              total: Yup.number().typeError("Total must be a number").required("Total is required"),
              accessories: Yup.array().of(
                Yup.object().shape({
                  accessory_id: Yup.mixed().nullable(),
                  accessory_name: Yup.string().nullable(),
                  accessories_type: Yup.string().when(
                    ["accessory_name", "accessory_id", "price", "quantity"],
                    (values, schema) => {
                      const [name, id, price, qty] = values;
                      if (name || id || price || qty) {
                        return schema.required("Price type is required");
                      }
                      return schema.nullable();
                    }
                  ),
                  quantity: Yup.number()
                    .typeError("Accessory quantity must be a number")
                    .transform((value, originalValue) => (originalValue === "" ? null : value))
                    .integer("Accessory quantity must be an integer")
                    .nullable(),
                  price: Yup.number().nullable(),
                  total: Yup.number().nullable(),
                })
              ),
            })
          )
          .min(1, "At least one product is required"),
      })
    )
    .min(1, "At least one item category is required"),

  note: Yup.string().nullable(),
});

const productSchema = Yup.object().shape({
  product_name: Yup.string().trim().required("Product name is required"),

  product_category_id: Yup.object().required("Product category is required"),

  product_code: Yup.string().trim().nullable(),

  product_active: Yup.boolean(),

  unit_price: Yup.number()
    .transform((v, o) => (o === "" ? undefined : Number(o)))
    .typeError("Unit price must be a number")
    .positive("Unit price must be greater than 0")
    .required("Unit price is required"),
  product_unit_id: Yup.object().required("Product unit is required"),

  taxable: Yup.boolean(),

  tax: Yup.number().nullable(),

  power_hp: Yup.number()
    .transform((v, o) => (o === "" || o === null ? undefined : Number(o)))
    .typeError("Power in HP must be a number")
    .min(0, "Power in HP cannot be negative")
    .nullable(),

  power_kw: Yup.number()
    .transform((v, o) => (o === "" || o === null ? undefined : Number(o)))
    .typeError("Power in KW must be a number")
    .min(0, "Power in KW cannot be negative")
    .nullable(),

  accessories: Yup.array().of(
    Yup.object().shape({
      accessory_name: Yup.string().trim().nullable(),
      accessories_type: Yup.string().when(
        ["accessory_name", "price", "quantity"],
        (values, schema) => {
          const [name, price, qty] = values;
          if (name || price || qty) {
            return schema.required("Price type is required");
          }
          return schema.nullable();
        }
      ),

      price: Yup.number()
        .transform((v, o) => (o === "" ? null : v))
        .typeError("Price must be a number")
        .min(0, "Price must be 0 or greater")
        .nullable(),

      quantity: Yup.number()
        .transform((v, o) => (o === "" ? null : v))
        .typeError("Quantity must be a number")
        .integer("Quantity must be an integer")
        .min(0, "Quantity must be 0 or greater")
        .nullable(),
    })
  ),

  description: Yup.string().nullable(),
  quotations_description: Yup.string().nullable(),
  scope_note: Yup.string().nullable(),
});

const productPackageSchema = Yup.object().shape({
  name: Yup.string().trim().required("Package name is required"),

  price: Yup.number()
    .typeError("Package price must be a number")
    .positive("Package price must be greater than 0")
    .required("Package price is required"),

  is_active: Yup.boolean(),

  items: Yup.array()
    .min(1, "At least one item is required")
    .of(
      Yup.object().shape({
        name: Yup.string().trim().required("Product category is required"),

        products: Yup.array()
          .min(1, "At least one product is required")
          .of(
            Yup.object().shape({
              product_id: Yup.mixed().required("Product is required"),

              quantity: Yup.number()
                .typeError("Quantity must be a number")
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .min(1, "Quantity must be at least 1")
                .required("Quantity is required"),

              price: Yup.number()
                .typeError("Price must be a number")
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .min(0, "Price must be 0 or greater")
                .required("Price is required"),

              total: Yup.number().typeError("Total must be a number").required("Total is required"),

              accessories: Yup.array().of(
                Yup.object().shape({
                  accessory_id: Yup.mixed().nullable(),
                  accessory_name: Yup.string().nullable(),
                  accessories_type: Yup.string().when(
                    ["accessory_name", "price", "quantity"],
                    (values, schema) => {
                      const [name, price, qty] = values;
                      if (name || price || qty) {
                        return schema.required("Price type is required");
                      }
                      return schema.nullable();
                    }
                  ),
                  quantity: Yup.number()
                    .typeError("Accessory quantity must be a number")
                    .transform((value, originalValue) => (originalValue === "" ? null : value))
                    .integer("Accessory quantity must be an integer")
                    .nullable(),
                  price: Yup.number().nullable(),
                  total: Yup.number().nullable(),
                })
              ),
            })
          ),
      })
    ),
});

const followUpSchema = Yup.object().shape({
  title: Yup.string().trim().required("Follow-up title is required"),

  followup_type: Yup.mixed().nullable().required("Follow-up type is required"),

  followup_status: Yup.mixed().nullable(),

  assigned_to: Yup.mixed().nullable(),

  related_to: Yup.mixed()
    .nullable()
    .when("related_type", {
      is: (val) => val !== "other",
      then: (schema) => schema.required("Related to is required"),
      otherwise: (schema) => schema.nullable(),
    }),

  due_date: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(new Date(), "Due date cannot be in the past")
    .required("Due date is required"),
});

export {
  roleSchema,
  userSchema,
  personSchema,
  leadSchema,
  quotationSchema,
  productSchema,
  productPackageSchema,
  followUpSchema,
  loginSchema,
  customerSchema,
};
